import React, { useState, useEffect } from 'react';
import humanChatService from '../../services/humanChatService';
import '../../styles/group-chat-brutal.css';

/**
 * Guide selector component for starting new chats
 * Shows available guides in the group
 */
const GuideSelector = ({
  groupId,
  onSelectGuide,
  onBack
}) => {
  const [guides, setGuides] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [selecting, setSelecting] = useState(null);

  // Load guides
  useEffect(() => {
    const loadGuides = async () => {
      if (!groupId) return;

      try {
        setLoading(true);
        setError(null);

        const data = await humanChatService.getGuides(groupId);

        if (data.success) {
          setGuides(data.guides || []);
        }
      } catch (err) {
        console.error('Failed to load guides:', err);
        setError(err.message);
      } finally {
        setLoading(false);
      }
    };

    loadGuides();
  }, [groupId]);

  // Handle guide selection
  const handleSelectGuide = async (guide) => {
    setSelecting(guide.userId);
    try {
      await onSelectGuide(guide);
    } finally {
      setSelecting(null);
    }
  };

  // Get initials for avatar
  const getInitials = (name) => {
    if (!name) return '?';
    return name.substring(0, 2).toUpperCase();
  };

  // Get role badges for display
  const getRoleBadges = (roles) => {
    const badges = [];
    if (roles?.includes('owner')) badges.push({ type: 'owner', label: 'OWNER' });
    if (roles?.includes('admin')) badges.push({ type: 'admin', label: 'ADMIN' });
    if (roles?.includes('guide')) badges.push({ type: 'guide', label: 'GUIDE' });
    return badges;
  };

  if (loading) {
    return (
      <div className="guide-selector">
        <div className="chat-thread-header">
          <button className="chat-thread-back" onClick={onBack} aria-label="Back">
            ←
          </button>
          <div className="chat-thread-info">
            <h3 className="chat-thread-name">SELECT GUIDE</h3>
          </div>
        </div>

        <div className="chat-loading">
          <div className="chat-loading-spinner" />
          <span>LOADING GUIDES...</span>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="guide-selector">
        <div className="chat-thread-header">
          <button className="chat-thread-back" onClick={onBack} aria-label="Back">
            ←
          </button>
          <div className="chat-thread-info">
            <h3 className="chat-thread-name">SELECT GUIDE</h3>
          </div>
        </div>

        <div className="chat-error">
          <div className="chat-error-icon">⚠️</div>
          <div className="chat-error-title">Failed to load</div>
          <div className="chat-error-text">{error}</div>
        </div>
      </div>
    );
  }

  if (guides.length === 0) {
    return (
      <div className="guide-selector">
        <div className="chat-thread-header">
          <button className="chat-thread-back" onClick={onBack} aria-label="Back">
            ←
          </button>
          <div className="chat-thread-info">
            <h3 className="chat-thread-name">SELECT GUIDE</h3>
          </div>
        </div>

        <div className="no-guides">
          <div className="no-guides-icon">👤</div>
          <div className="no-guides-title">No guides available</div>
          <div className="no-guides-text">
            Ask the group owner to assign guides so you can get personalized help.
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="guide-selector">
      <div className="chat-thread-header">
        <button className="chat-thread-back" onClick={onBack} aria-label="Back">
          ←
        </button>
        <div className="chat-thread-info">
          <h3 className="chat-thread-name">SELECT GUIDE</h3>
        </div>
      </div>

      <div style={{ padding: '20px' }}>
        <div className="guide-selector-title">Chat with a Guide</div>
        <div className="guide-selector-subtitle">
          Get personalized help with your investments from group guides.
        </div>

        <div className="guide-list">
          {guides.map((guide) => {
            const displayName = guide.nickname || guide.username;
            const badges = getRoleBadges(guide.roles);
            const isSelecting = selecting === guide.userId;

            return (
              <div
                key={guide.userId}
                className="guide-card"
                onClick={() => !isSelecting && handleSelectGuide(guide)}
                style={{ cursor: isSelecting ? 'wait' : 'pointer' }}
              >
                <div className="guide-avatar">
                  {guide.avatar ? (
                    <img
                      src={guide.avatar}
                      alt={displayName}
                      style={{ width: '100%', height: '100%', objectFit: 'cover' }}
                    />
                  ) : (
                    getInitials(displayName)
                  )}
                </div>

                <div className="guide-info">
                  <div className="guide-name">
                    {displayName}
                    {badges.map((badge) => (
                      <span key={badge.type} className={`role-badge ${badge.type}`}>
                        {badge.label}
                      </span>
                    ))}
                  </div>
                  <div className="guide-status">
                    Member since {new Date(guide.joinedAt).toLocaleDateString()}
                  </div>
                </div>

                <button
                  className="guide-start-btn"
                  disabled={isSelecting}
                >
                  {isSelecting ? '...' : 'CHAT'}
                </button>
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
};

export default GuideSelector;
